import { Component } from '@theme/component';
import {
  findLargestInteger,
  getPageUrl,
  getRequestedPage,
  parsePositiveInteger,
} from './bloomreach-load-more-utils.js';

const HOST_SELECTOR = '.br-widget-host--collections, .br-widget-host--search';
const GRID_SELECTOR = '.br-products__grid';
const PAGINATION_SELECTOR = '.br-pagination';
const ACTIVE_PAGE_SELECTOR = '.br-pagination__page--active, [aria-current="page"]';
const PAGE_PARAMETER = 'load_more_page';
// The widget's own history integration writes this parameter when its
// next-page control is activated. It must never survive in a load-more URL:
// on reload the widget boots straight to that page, page 1 is lost, and the
// replay logic wrongly reports every product as shown (observed live with
// ?page=2&load_more_page=2 rendering 16 of 39 results as "All products
// shown"). When the parameter arrives in the entry URL, the shopper followed
// a deep link into the widget's own paging — leave numbered pagination alone.
const WIDGET_PAGE_PARAMETER = 'page';
const LOAD_TIMEOUT = 15000;
// The visual hold makes the retained cards inert (pointer-events: none on the
// holder). If completion is ever missed, LOAD_TIMEOUT is far too long to leave
// visible product cards unclickable — observed live on iOS: load more never
// completed and every card was dead for the full 15s before #failLoad restored
// them. The hold therefore has its own, much shorter budget: once it expires
// the cards go back into the live grid and stay interactive while the load
// continues in the background.
const HOLD_TIMEOUT = 2000;
// Card shadow roots fill after the light-DOM swap, unseen by the observer,
// so an in-flight load also re-checks on this interval (bounded by LOAD_TIMEOUT).
const COMPLETION_POLL_INTERVAL = 150;
const GRID_ID = 'BloomreachProductGrid';

/**
 * @typedef {object} BloomreachLoadMoreRefs
 * @property {HTMLButtonElement} button - Load-more action.
 * @property {HTMLElement} buttonLabel - Visible action label.
 * @property {HTMLElement} progress - Optional showing-count text.
 * @property {HTMLElement} status - Polite loading announcements.
 */

const PRODUCT_CARD_BODY_SELECTOR = '.ceres-card, .br-product-card, [data-pid]';

/**
 * Converts Bloomreach's numbered pagination into an append-only load-more
 * interaction while leaving Bloomreach responsible for search state.
 * @extends {Component<BloomreachLoadMoreRefs>}
 */
class BloomreachLoadMore extends Component {
  requiredRefs = ['button', 'buttonLabel', 'progress', 'status'];

  /** @type {HTMLElement | null} */
  #host = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {Element[]} */
  #retainedItems = [];

  /** @type {number} */
  #currentPage = 1;

  /** @type {number} */
  #restoreTarget = 1;

  /** @type {number | undefined} */
  #loadTimeout;

  /** @type {number | undefined} */
  #holdTimeout;

  /** @type {Set<string>} */
  #retainedProductIds = new Set();

  /** @type {string} */
  #hrefBeforeLoad = '';

  /** @type {number} */
  #scrollPosition = 0;

  /** @type {number | null} */
  #restoreScrollPosition = null;

  #isLoading = false;
  #userInitiatedLoad = false;
  #initialised = false;
  #isMoving = false;
  #hasRestoredScroll = false;

  /** @type {HTMLElement | null} */
  #holder = null;

  /** @type {ShadowRoot | null} */
  #observedShadowRoot = null;

  /** @type {number | undefined} */
  #completionPoll;

  /** @returns {void} */
  connectedCallback() {
    super.connectedCallback();
    if (this.#initialised) return;

    this.#initialised = true;
    this.#restoreTarget = getRequestedPage(window.location.href, PAGE_PARAMETER);
    const savedScrollPosition = Number(window.history.state?.bloomreachLoadMoreScrollPosition);
    this.#restoreScrollPosition =
      Number.isFinite(savedScrollPosition) && savedScrollPosition >= 0 ? savedScrollPosition : null;
    window.addEventListener('popstate', this.#onPopState);
    this.connectToHost();
  }

  /** @returns {void} */
  disconnectedCallback() {
    if (this.#isMoving) return;

    // Living inside the widget's layout tree means its re-renders detach this
    // element. That is a re-anchor, not a teardown: once the fresh subtree
    // settles, insert against it and resynchronise. Only a host that left the
    // document (section removed, page teardown) disposes the component.
    const host = this.#host;
    if (host?.isConnected) {
      this.#isMoving = true;
      queueMicrotask(() => {
        this.#isMoving = false;
        if (!this.isConnected && host.isConnected) this.#enhance();
      });
      return;
    }

    super.disconnectedCallback();
    this.#observer?.disconnect();
    window.removeEventListener('popstate', this.#onPopState);
    if (this.#loadTimeout) window.clearTimeout(this.#loadTimeout);
    if (this.#holdTimeout) window.clearTimeout(this.#holdTimeout);
    this.#stopCompletionPoll();
    this.#initialised = false;
  }

  /**
   * Loads and appends the next Bloomreach result page.
   * @returns {void}
   */
  loadMore() {
    this.#startLoad(true);
  }

  /** Finds the app host before observing it. Not private (SM-542). @returns {void} */
  connectToHost() {
    if (getRequestedPage(window.location.href, WIDGET_PAGE_PARAMETER) > 1) {
      // Deep link into the widget's own paging — its own control must stay visible.
      this.hidden = true;
      return;
    }

    this.#observer?.disconnect();
    this.#observedShadowRoot = null;

    const host = document.querySelector(HOST_SELECTOR);
    if (host instanceof HTMLElement) {
      this.#host = host;
      this.#observer = new MutationObserver(() => this.#enhance());
      this.#observer.observe(host, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'aria-current', 'disabled', 'aria-disabled', 'data-total-results'],
      });
      this.#enhance();
      return;
    }

    this.hidden = true;
  }

  /**
   * Synchronises the theme control with Bloomreach's latest rendered page.
   * @returns {void}
   */
  #enhance() {
    const host = this.#host;
    if (!host) return;

    const grid = host.querySelector(GRID_SELECTOR);
    const pagination = host.querySelector(PAGINATION_SELECTOR);
    if (!(grid instanceof HTMLElement)) return;

    this.#observeShadowPagination(pagination);
    this.#moveAfterHost(host);
    if (!grid.id) grid.id = GRID_ID;
    this.refs.button.setAttribute('aria-controls', grid.id);

    if (this.#isLoading) {
      this.#hideSourcePagination(pagination);
      this.#tryCompleteLoad(grid, pagination);
      return;
    }

    const activePage = this.#getActivePage(pagination) ?? 1;
    if (activePage < this.#currentPage) {
      this.#restoreTarget = activePage;
      this.#replacePageState(activePage);
    }
    this.#currentPage = activePage;

    const nextControl = this.#getNextControl(pagination, activePage);
    if (!this.#canEnhancePagination(pagination, activePage, nextControl)) {
      this.#showSourcePagination(pagination);
      this.hidden = true;
      return;
    }

    this.#hideSourcePagination(pagination);
    this.#updateProgress(grid);
    this.#updateAvailability(Boolean(nextControl));

    if (this.#restoreTarget > activePage && nextControl) {
      queueMicrotask(() => this.#startLoad(false));
    } else if (this.#restoreTarget > activePage) {
      this.#restoreTarget = activePage;
      this.#replacePageState(activePage);
    } else {
      this.#restoreSavedScrollPosition();
    }
  }

  /**
   * Moves the component below the persistent app host without reinitialising it.
   * @param {HTMLElement} host - Bloomreach app-block host.
   * @returns {void}
   */
  #moveAfterHost(host) {
    // NEVER anchor inside the widget's managed DOM: Loomi ejects foreign
    // children, the disconnect handler re-inserted, and the two looped until
    // the page froze (observed live on the Ceres preview). Sit as the host's
    // next sibling; the snippet's stylesheet wraps the panel so this takes
    // its own full-width row below the widget.
    if (this.previousElementSibling === host) return;

    this.#isMoving = true;
    host.after(this);
    queueMicrotask(() => {
      this.#isMoving = false;
    });
  }

  /**
   * Starts the widget's own next-page action so its facet and sort state is retained.
   * @param {boolean} userInitiated - Whether a shopper activated the button.
   * @returns {void}
   */
  #startLoad(userInitiated) {
    if (this.#isLoading) return;

    const host = this.#host;
    const grid = host?.querySelector(GRID_SELECTOR);
    const pagination = host?.querySelector(PAGINATION_SELECTOR);
    if (!(grid instanceof HTMLElement)) return;

    const activePage = this.#getActivePage(pagination) ?? this.#currentPage;
    const nextControl = this.#getNextControl(pagination, activePage);
    if (!(nextControl instanceof HTMLElement)) {
      this.#updateAvailability(false);
      return;
    }

    this.#isLoading = true;
    this.#userInitiatedLoad = userInitiated;
    this.#retainedItems = Array.from(grid.children);
    this.#retainedProductIds = this.#productIdsOf(this.#retainedItems);
    this.#currentPage = activePage;
    this.#hrefBeforeLoad = window.location.href;
    this.#scrollPosition = window.scrollY;
    this.refs.button.setAttribute('aria-busy', 'true');
    this.refs.button.setAttribute('aria-disabled', 'true');
    this.refs.buttonLabel.textContent = this.dataset.loadingLabel ?? '';
    this.refs.status.textContent = this.dataset.loadingAnnouncement ?? '';

    this.#holdRetainedItems(host, grid);
    nextControl.click();
    this.#holdTimeout = window.setTimeout(() => this.#endHold(), HOLD_TIMEOUT);
    this.#loadTimeout = window.setTimeout(() => this.#failLoad(), LOAD_TIMEOUT);
    this.#stopCompletionPoll();
    this.#completionPoll = window.setInterval(() => this.#enhance(), COMPLETION_POLL_INTERVAL);
  }

  /**
   * Stops re-checking for completion between observer callbacks.
   * @returns {void}
   */
  #stopCompletionPoll() {
    if (this.#completionPoll) window.clearInterval(this.#completionPoll);
    this.#completionPoll = undefined;
  }

  /**
   * Keeps the loaded cards visible while the widget renders the next page.
   *
   * The widget wipes its grid the moment the next page is requested; without
   * a visible hold every loaded product vanishes for the whole network
   * round-trip and reappears on completion — a jarring flash. The holder
   * rebuilds the grid's ancestor shells (cloned WITHOUT content so the
   * widget's descendant-scoped CSS still applies, with the host-level marker
   * classes stripped so nothing binds to the clone) and adopts the real card
   * elements. The live host is collapsed via a pending class; completion
   * moves the same nodes back into the live grid before paint, so the merge
   * is seamless.
   *
   * @param {HTMLElement} host - Widget host element.
   * @param {HTMLElement} grid - Live widget grid.
   * @returns {void}
   */
  #holdRetainedItems(host, grid) {
    const shells = [];
    for (let el = grid; el instanceof HTMLElement; el = el.parentElement) {
      shells.unshift(el);
      if (el === host) break;
    }
    if (shells[0] !== host) return;

    /** @type {HTMLElement | null} */
    let holderRoot = null;
    /** @type {HTMLElement | null} */
    let innerShell = null;
    for (const source of shells) {
      const shell = /** @type {HTMLElement} */ (source.cloneNode(false));
      shell.removeAttribute('id');
      if (innerShell) innerShell.append(shell);
      else holderRoot = shell;
      innerShell = shell;
    }
    if (!holderRoot || !innerShell) return;

    for (const marker of Array.from(holderRoot.classList)) {
      if (marker.startsWith('br-widget-host') || marker.startsWith('shopify-')) holderRoot.classList.remove(marker);
    }
    holderRoot.classList.add('bloomreach-load-more-holder');
    holderRoot.setAttribute('aria-hidden', 'true');
    innerShell.append(...this.#retainedItems);
    this.#holder = holderRoot;
    host.before(holderRoot);
    host.classList.add('bloomreach-load-more-pending');
  }

  /**
   * Removes the holding shell and restores the live host's visibility.
   * @returns {void}
   */
  #releaseHold() {
    if (this.#holdTimeout) window.clearTimeout(this.#holdTimeout);
    this.#holdTimeout = undefined;
    this.#holder?.remove();
    this.#holder = null;
    this.#host?.classList.remove('bloomreach-load-more-pending');
  }

  /**
   * Ends the visual hold early, without ending the load.
   *
   * The hold trades interactivity for a flash-free transition, and that trade
   * is only acceptable for the length of a render. When the widget has not
   * settled by HOLD_TIMEOUT the trade stops being worth it: put the retained
   * cards back in the live grid, drop the inert holder, and let the load keep
   * running. A late completion still merges correctly — #restoreRetainedItems
   * is a no-op once the cards are already in the grid.
   *
   * @returns {void}
   */
  #endHold() {
    this.#holdTimeout = undefined;
    if (!this.#holder) return;

    this.#restoreRetainedItems();
    this.#releaseHold();
  }

  /**
   * Moves the retained cards back into the live grid if they are not there.
   * @returns {void}
   */
  #restoreRetainedItems() {
    const grid = this.#host?.querySelector(GRID_SELECTOR);
    if (!(grid instanceof HTMLElement)) return;
    if (this.#retainedItems.every((item) => item.parentElement === grid)) return;

    const retainedFragment = document.createDocumentFragment();
    retainedFragment.append(...this.#retainedItems);
    grid.prepend(retainedFragment);
  }

  /**
   * Collects the widget's product identifiers for a set of grid children.
   * @param {Element[]} items - Grid children.
   * @returns {Set<string>} Product identifiers present in those children.
   */
  #productIdsOf(items) {
    /** @type {Set<string>} */
    const ids = new Set();
    for (const item of items) {
      const id = this.#productIdOf(item);
      if (id) ids.add(id);
    }
    return ids;
  }

  /**
   * Reads a grid child's product identifier. OBSERVED (live, 2 Sep 2026):
   * the light-DOM `br-product-card` host has no attributes; the id sits in
   * its shadow root on `[data-pid]` (or `[data-product-url]`), and only once
   * the widget has rendered that card.
   * @param {Element} item - Grid child.
   * @returns {string | null} Product identifier, or null while unrendered.
   */
  #productIdOf(item) {
    if (!item.matches('br-product-card')) return null;
    const lightId = item.getAttribute('data-pid')?.trim();
    if (lightId) return lightId;

    const shadowRoot = item.shadowRoot;
    const shadowId = shadowRoot?.querySelector('[data-pid]')?.getAttribute('data-pid')?.trim();
    if (shadowId) return shadowId;

    const productUrl = shadowRoot?.querySelector('[data-product-url]')?.getAttribute('data-product-url')?.trim();
    return productUrl || null;
  }

  /**
   * Reports whether the widget has rendered products it was not showing before.
   *
   * The completion signal is read from the GRID, not the active page.
   * OBSERVED (iOS Safari, live, 25 Aug 2026): gating on the active page hung
   * every load, since the page number lives in a `br-pagination` shadow root
   * the widget replaces wholesale, which the observer can miss. New product
   * ids land in the light DOM where it cannot.
   *
   * @param {Element[]} items - Grid children that were not retained.
   * @returns {boolean} Whether a genuinely new page has rendered.
   */
  #hasNewProducts(items) {
    const ids = items.map((item) => this.#productIdOf(item)).filter((id) => id !== null);

    // OBSERVED (live, 2 Sep 2026): ~100ms after the next click the widget
    // swaps in EMPTY card shells that render later. Treating them as a new
    // page completed instantly, restored page 1 into a grid about to be
    // wiped, and left the shopper with page 2 alone. Unidentified cards are
    // never a completion signal; the poll re-checks once they render.
    if (ids.length === 0) return false;

    return ids.some((id) => !this.#retainedProductIds.has(id));
  }

  /**
   * Completes an append after Bloomreach has rendered the expected page.
   * @param {HTMLElement} grid - Latest Bloomreach products grid.
   * @param {Element | null} pagination - Latest Bloomreach pagination.
   * @returns {void}
   */
  #tryCompleteLoad(grid, pagination) {
    const retainedSet = new Set(this.#retainedItems);
    const newItems = Array.from(grid.children).filter((item) => !retainedSet.has(item));
    if (!this.#hasNewProducts(newItems)) return;

    // The active page stays authoritative WHEN the widget exposes it — that
    // keeps multi-page restoration counting correctly — but it is no longer a
    // precondition, so a missed shadow-root update cannot hang the load.
    const activePage = this.#getActivePage(pagination);
    const expectedPage = activePage !== null && activePage > this.#currentPage ? activePage : this.#currentPage + 1;

    this.#restoreRetainedItems();

    if (this.#loadTimeout) window.clearTimeout(this.#loadTimeout);
    this.#loadTimeout = undefined;
    this.#stopCompletionPoll();
    this.#isLoading = false;
    this.#currentPage = expectedPage;
    this.#retainedItems = [];
    this.#retainedProductIds = new Set();
    this.#releaseHold();
    const restoredPage = expectedPage >= this.#restoreTarget;
    const scrollPosition =
      !this.#userInitiatedLoad && restoredPage && this.#restoreScrollPosition !== null
        ? this.#restoreScrollPosition
        : this.#scrollPosition;
    window.scrollTo({ top: scrollPosition, behavior: 'auto' });

    this.#writePageState(expectedPage);
    // The widget's own history write races ours; if it lands after this
    // task and reintroduces its page parameter, sweep it up once settled.
    window.setTimeout(() => {
      if (getRequestedPage(window.location.href, WIDGET_PAGE_PARAMETER) > 1) {
        window.history.replaceState(window.history.state, '', this.#pageStateUrl(this.#currentPage));
      }
    }, 100);
    this.#updateProgress(grid);
    const nextControl = this.#getNextControl(pagination, expectedPage);
    this.#updateAvailability(Boolean(nextControl));
    this.refs.status.textContent = this.#loadedAnnouncement(newItems);

    if (this.#userInitiatedLoad) this.refs.button.focus({ preventScroll: true });
    if (restoredPage) this.#restoreSavedScrollPosition();
    this.#userInitiatedLoad = false;

    if (this.#restoreTarget > expectedPage && nextControl) {
      queueMicrotask(() => this.#startLoad(false));
    }
  }

  /**
   * Restores the retained page and exposes a retry when Bloomreach does not settle.
   * @returns {void}
   */
  #failLoad() {
    this.#restoreRetainedItems();
    this.#releaseHold();
    this.#stopCompletionPoll();
    this.#loadTimeout = undefined;
    this.#isLoading = false;
    this.#retainedItems = [];
    this.#retainedProductIds = new Set();
    this.refs.button.removeAttribute('aria-busy');
    this.refs.button.removeAttribute('aria-disabled');
    this.refs.buttonLabel.textContent = this.dataset.buttonLabel ?? '';
    this.refs.status.textContent = this.dataset.errorAnnouncement ?? '';
    window.scrollTo({ top: this.#scrollPosition, behavior: 'auto' });
    if (this.#userInitiatedLoad) this.refs.button.focus({ preventScroll: true });
    this.#userInitiatedLoad = false;
  }

  /**
   * Resolves the DOM scope that actually holds the pagination controls.
   *
   * The widget renders its controls inside a `br-pagination` custom element
   * with an OPEN shadow root (verified on the live Ceres store) — light-DOM
   * queries on the container come back empty, which made this component
   * disable itself everywhere. When that element is present, query its shadow
   * root; otherwise fall back to the container for widget versions that render
   * controls in the light DOM.
   *
   * @param {Element | null} pagination - Bloomreach pagination container.
   * @returns {Element | ShadowRoot | null} Scope to query for controls.
   */
  #paginationScope(pagination) {
    if (!pagination) return null;
    const shadowHost = pagination.querySelector('br-pagination');
    return shadowHost?.shadowRoot ?? pagination;
  }

  /**
   * Watches the pagination shadow root, where the active-page flip happens.
   *
   * The host observer cannot see shadow-DOM mutations, so without this the
   * component only learns of a page change when the light-DOM grid children
   * are replaced. Observing the shadow root directly removes that ordering
   * assumption. Safe against observer loops: this component never writes into
   * the shadow root (hide/show target the light-DOM container). Re-attached
   * whenever the widget re-renders a fresh `br-pagination` element.
   *
   * @param {Element | null} pagination - Bloomreach pagination container.
   * @returns {void}
   */
  #observeShadowPagination(pagination) {
    const scope = this.#paginationScope(pagination);
    if (!(scope instanceof ShadowRoot) || scope === this.#observedShadowRoot || !this.#observer) return;
    this.#observer.observe(scope, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'aria-current', 'disabled', 'aria-disabled', 'data-total-results'],
    });
    this.#observedShadowRoot = scope;
  }

  /**
   * Returns Bloomreach's active page number.
   * @param {Element | null} pagination - Bloomreach pagination container.
   * @returns {number | null} Active page number.
   */
  #getActivePage(pagination) {
    const scope = this.#paginationScope(pagination);
    return parsePositiveInteger(scope?.querySelector(ACTIVE_PAGE_SELECTOR)?.textContent?.trim());
  }

  /**
   * Finds the next numeric page, falling back to Bloomreach's trailing next button.
   * @param {Element | null} pagination - Bloomreach pagination container.
   * @param {number} activePage - Current page number.
   * @returns {HTMLElement | null} Widget control that requests the next page.
   */
  #getNextControl(pagination, activePage) {
    const scope = this.#paginationScope(pagination);
    if (!scope) return null;

    const controls = Array.from(scope.querySelectorAll('button, a')).filter(
      (control) => control instanceof HTMLElement,
    );
    const numericControl = controls.find(
      (control) => !this.#isDisabled(control) && parsePositiveInteger(control.textContent?.trim()) === activePage + 1,
    );
    if (numericControl instanceof HTMLElement) return numericControl;

    const navigationControls = controls.filter((control) => control.classList.contains('br-pagination__button'));
    const trailingControl = navigationControls.at(-1);
    return trailingControl instanceof HTMLElement && !this.#isDisabled(trailingControl) ? trailingControl : null;
  }

  /**
   * Confirms the numbered-pagination contract before hiding the fallback UI.
   * @param {Element | null} pagination - Bloomreach pagination container.
   * @param {number} activePage - Current page number.
   * @param {HTMLElement | null} nextControl - Resolved next-page control.
   * @returns {boolean} Whether the load-more controller can safely take over.
   */
  #canEnhancePagination(pagination, activePage, nextControl) {
    const scope = this.#paginationScope(pagination);
    if (!scope) return false;
    if (nextControl) return true;

    const numberedPages = Array.from(scope.querySelectorAll('.br-pagination__page'))
      .map((control) => parsePositiveInteger(control.textContent?.trim()))
      .filter((page) => page !== null);
    return numberedPages.length > 0 && Math.max(...numberedPages) <= activePage;
  }

  /**
   * @param {Element | null} pagination - Bloomreach pagination container.
   * @returns {void}
   */
  #hideSourcePagination(pagination) {
    if (!(pagination instanceof HTMLElement)) return;
    // Every write here MUST be conditional. This element sits inside the
    // observed subtree with 'class' in the attributeFilter, and classList.add
    // of an already-present token still re-sets the class attribute, which
    // queues a mutation record — an unconditional write loops the observer
    // into an infinite microtask chain that freezes the page.
    if (!pagination.classList.contains('br-pagination--load-more-source')) {
      pagination.classList.add('br-pagination--load-more-source');
    }
    if (pagination.getAttribute('aria-hidden') !== 'true') pagination.setAttribute('aria-hidden', 'true');
  }

  /**
   * @param {Element | null} pagination - Bloomreach pagination container.
   * @returns {void}
   */
  #showSourcePagination(pagination) {
    if (!(pagination instanceof HTMLElement)) return;
    // Conditional for the same observer-loop reason as #hideSourcePagination.
    if (pagination.classList.contains('br-pagination--load-more-source')) {
      pagination.classList.remove('br-pagination--load-more-source');
    }
    if (pagination.hasAttribute('aria-hidden')) pagination.removeAttribute('aria-hidden');
  }

  /**
   * Checks the widget's disabled-state conventions.
   * @param {HTMLElement} control - Pagination control.
   * @returns {boolean} Whether the control is unavailable.
   */
  #isDisabled(control) {
    return (
      control.hasAttribute('disabled') ||
      control.getAttribute('aria-disabled') === 'true' ||
      control.classList.contains('br-pagination__button--disabled')
    );
  }

  /**
   * Shows the result total only when the widget exposes one in its rendered DOM.
   * @param {HTMLElement} grid - Bloomreach products grid.
   * @returns {void}
   */
  #updateProgress(grid) {
    const shown = this.#countProductCards(Array.from(grid.children));
    const total = this.#readTotal();
    if (shown === null || total === null || total < shown) {
      this.refs.progress.hidden = true;
      this.refs.progress.textContent = '';
      return;
    }

    this.refs.progress.textContent = this.#interpolate(this.dataset.viewedLabel, {
      count: String(shown),
      total: String(total),
    });
    this.refs.progress.hidden = false;
  }

  /**
   * Reads a total from the widget's result-count surfaces without inventing one.
   *
   * The collections widget exposes no `__count-display` of its own — only the
   * search widget does — so a host-only lookup left the viewed count hidden on
   * every collection PLP (SM-530). The fallbacks walk outwards from the most
   * widget-owned surface to the least: the widget's own count, then the count
   * the filters drawer derives from the widget's live apply control, then the
   * theme toolbar's native `collection.products_count`.
   *
   * @returns {number | null} Exposed result total.
   */
  #readTotal() {
    const widgetTotal = this.#readCountFrom(
      this.#host?.querySelector(
        '.br-search-widget__count-display, .br-collections-widget__count-display, ' +
          '.br-collection-widget__count-display, [data-total-results]',
      ),
    );
    if (widgetTotal !== null) return widgetTotal;

    const drawerTotal = parsePositiveInteger(
      /** @type {HTMLElement | null} */ (document.querySelector('[data-br-result-count]'))?.dataset.brResultCount,
    );
    if (drawerTotal !== null) return drawerTotal;

    const toolbarTotal = parsePositiveInteger(
      /** @type {HTMLElement | null} */ (document.querySelector('facets-form-component[data-results-count]'))?.dataset
        .resultsCount,
    );
    if (toolbarTotal !== null) return toolbarTotal;

    return this.#readCountFrom(document.querySelector('[data-testid="products-count"]'));
  }

  /**
   * Reads a positive integer from a result-count element's data or label.
   * @param {Element | null | undefined} element - Candidate count surface.
   * @returns {number | null} Exposed count.
   */
  #readCountFrom(element) {
    if (!(element instanceof HTMLElement)) return null;

    const dataTotal = parsePositiveInteger(element.dataset.totalResults);
    return dataTotal ?? findLargestInteger(element.textContent ?? '');
  }

  /**
   * Counts product cards while excluding Bloomreach content-injection cards.
   * @param {Element[]} items - Grid children.
   * @returns {number | null} Product count, or null when card identity is unavailable.
   */
  #countProductCards(items) {
    const cards = items.filter((item) => item.matches('br-product-card'));
    if (cards.some((card) => card.hasAttribute('data-pid'))) {
      return cards.filter((card) => card.getAttribute('data-pid')?.trim()).length;
    }

    const rendered = cards.filter((card) => (card.shadowRoot ?? card).querySelector(PRODUCT_CARD_BODY_SELECTOR));
    return rendered.length > 0 ? rendered.length : null;
  }

  /**
   * Builds the polite completion message for a rendered page.
   * @param {Element[]} items - Newly rendered grid children.
   * @returns {string} Completion announcement.
   */
  #loadedAnnouncement(items) {
    const loaded = this.#countProductCards(items);
    if (loaded === null) return this.dataset.loadedAnnouncement ?? '';
    return this.#interpolate(this.dataset.loadedCountAnnouncement, { count: String(loaded) });
  }

  /**
   * Updates button state while keeping the final disabled control focusable.
   * @param {boolean} hasNextPage - Whether Bloomreach exposes another page.
   * @returns {void}
   */
  #updateAvailability(hasNextPage) {
    this.hidden = false;
    this.refs.button.removeAttribute('aria-busy');
    this.refs.button.setAttribute('aria-disabled', String(!hasNextPage));
    this.refs.buttonLabel.textContent = hasNextPage ? this.dataset.buttonLabel ?? '' : this.dataset.completeLabel ?? '';
  }

  /**
   * Updates history without dropping Bloomreach's facet/search parameters.
   * @param {number} page - Latest appended page.
   * @returns {void}
   */
  /**
   * Builds the canonical load-more URL: our page parameter set, the widget's
   * own page parameter stripped (see WIDGET_PAGE_PARAMETER).
   * @param {number} page - Current appended page.
   * @returns {string} The updated absolute URL.
   */
  #pageStateUrl(page) {
    const url = new URL(getPageUrl(window.location.href, PAGE_PARAMETER, page));
    url.searchParams.delete(WIDGET_PAGE_PARAMETER);
    return url.toString();
  }

  #writePageState(page) {
    const nextUrl = this.#pageStateUrl(page);
    const savedScrollPosition = this.#userInitiatedLoad
      ? this.#scrollPosition
      : this.#restoreScrollPosition ?? this.#scrollPosition;
    const state = {
      ...window.history.state,
      bloomreachLoadMorePage: page,
      bloomreachLoadMoreScrollPosition: savedScrollPosition,
    };
    const widgetUpdatedUrl = window.location.href !== this.#hrefBeforeLoad;
    const method = this.#userInitiatedLoad && !widgetUpdatedUrl ? 'pushState' : 'replaceState';
    window.history[method](state, '', nextUrl);
    this.#restoreScrollPosition = savedScrollPosition;
  }

  /**
   * Replaces page state after a facet reset or an unreachable restoration target.
   * @param {number} page - Current rendered page.
   * @returns {void}
   */
  #replacePageState(page) {
    const nextUrl = this.#pageStateUrl(page);
    const scrollPosition = window.scrollY;
    window.history.replaceState(
      {
        ...window.history.state,
        bloomreachLoadMorePage: page,
        bloomreachLoadMoreScrollPosition: scrollPosition,
      },
      '',
      nextUrl,
    );
    this.#restoreScrollPosition = scrollPosition;
  }

  /**
   * Replaces bracketed translation placeholders.
   * @param {string | undefined} template - Rendered translation template.
   * @param {Record<string, string>} values - Placeholder values.
   * @returns {string} Interpolated text.
   */
  #interpolate(template, values) {
    let output = template ?? '';
    for (const [key, value] of Object.entries(values)) output = output.replaceAll(`[${key}]`, value);
    return output;
  }

  /**
   * Restores the saved history position after all requested pages exist.
   * @returns {void}
   */
  #restoreSavedScrollPosition() {
    if (this.#hasRestoredScroll || this.#restoreScrollPosition === null) return;

    this.#hasRestoredScroll = true;
    requestAnimationFrame(() => {
      window.scrollTo({ top: this.#restoreScrollPosition ?? 0, behavior: 'auto' });
    });
  }

  /**
   * Reloads a different history page so its append sequence can be reconstructed.
   * @returns {void}
   */
  #onPopState = () => {
    const requestedPage = getRequestedPage(window.location.href, PAGE_PARAMETER);
    if (requestedPage !== this.#currentPage) window.location.reload();
  };
}

if (!customElements.get('bloomreach-load-more')) {
  customElements.define('bloomreach-load-more', BloomreachLoadMore);
}
